import React from "react";

/**
 * Reusable ProgressBar component for dashboards.
 * 
 * @param {string} label - The title/name of the metric.
 * @param {number} value - The current value (used in display).
 * @param {number} total - The total value (optional, used if `percentage` is not provided).
 * @param {number} percentage - The explicit percentage (0-100).
 * @param {string} color - The fill color of the progress bar.
 * @param {boolean} showValue - Whether to display the value/percentage text.
 * @param {string} size - Height of the bar: 'xs', 'sm', 'md', 'lg'.
 * @param {string} labelSize - Custom font size for the label.
 * @param {string} valueSize - Custom font size for the value.
 */
const ProgressBar = ({
    label,
    value,
    total,
    percentage,
    color = "#6366F1",
    showValue = true,
    size = "sm",
    labelSize = "text-sm",
    valueSize = "text-sm"
}) => {
    // Calculate percentage if not explicitly provided
    const calculatedPercentage = percentage !== undefined
        ? percentage
        : (total ? Math.round((value / total) * 100) : 0);

    // Height mapping
    const heightClass = {
        xs: "h-1",
        sm: "h-1.5",
        md: "h-2",
        lg: "h-3"
    }[size] || "h-1.5";

    return (
        <div className="w-full mb-4 last:mb-0">
            {(label || showValue) && (
                <div className="flex justify-between items-center mb-1.5">
                    {label && (
                        <span className={`${labelSize} font-semibold text-gray-700`}>
                            {label}
                        </span>
                    )}
                    {showValue && (
                        <span className={`${valueSize} font-bold text-gray-900`}>
                            {value !== undefined ? value : `${calculatedPercentage}%`}
                            {total !== undefined && <span className="text-gray-400 font-medium ml-1 text-[10px]">/ {total}</span>}
                        </span>
                    )}
                </div>
            )}

            <div className={`w-full bg-gray-50 rounded-full ${heightClass} overflow-hidden shadow-inner`}>
                <div
                    className="h-full rounded-full transition-all duration-1000 ease-out"
                    style={{
                        width: `${Math.min(100, Math.max(0, calculatedPercentage))}%`,
                        backgroundColor: color
                    }}
                />
            </div>
        </div>
    );
};

export default ProgressBar;
